const JSON_FIELDS = [
  'sale_associate',
  'products_list',
  'client_contact',
  'invoicing_address',
  'delivery_address',
  'access_information',
  'store_information',
  'interior_designer',
  'delivery_groups',
] as const;

const STORE_BANK_FIELD_KEYS = [
  'account_holder',
  'bank',
  'account_number',
  'swift',
  'iban',
  'bank_address',
] as const;

const DEFAULT_DELIVERY_GROUPS = [
  { name: 'A', date: '' },
  { name: 'B', date: '' },
  { name: 'C', date: '' },
];

const JSON_FIELD_DEFAULTS: Record<(typeof JSON_FIELDS)[number], unknown> = {
  sale_associate: { name: '', phone: '', email: '' },
  products_list: [],
  client_contact: { name: '', client_id: '', billing_address: {}, delivery_address: {} },
  invoicing_address: {},
  delivery_address: {},
  access_information: {},
  store_information: { country: { name: '' }, deposit: 50 },
  interior_designer: { name: '', email: '', phone: '' },
  delivery_groups: DEFAULT_DELIVERY_GROUPS,
};

function cloneDefault<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

export function normalizeStoreInformationBankFields(si: any) {
  if (!si || typeof si !== 'object') return;
  let bi: any = si.bank_information;
  if (typeof bi === 'string') {
    try {
      bi = JSON.parse(bi);
    } catch {
      bi = null;
    }
  }
  if (bi && typeof bi === 'object') {
    for (const key of STORE_BANK_FIELD_KEYS) {
      const nested = bi[key];
      const flat = si[key];
      if (nested != null && nested !== '' && (flat == null || flat === '')) {
        si[key] = nested;
      }
    }
  }
  delete si.bank_information;
}

function ensureSaleAssociateDefaults(quote: any) {
  if (!quote.sale_associate || typeof quote.sale_associate !== 'object') {
    quote.sale_associate = { name: '', phone: '', email: '' };
    return;
  }
  quote.sale_associate = {
    ...quote.sale_associate,
    name: quote.sale_associate.name || '',
    phone: quote.sale_associate.phone || '',
    email: quote.sale_associate.email || '',
  };
}

function ensureStoreInformationDefaults(quote: any) {
  if (!quote.store_information || typeof quote.store_information !== 'object') {
    quote.store_information = { country: { name: '' }, deposit: 50 };
    return;
  }
  if (!quote.store_information.country || typeof quote.store_information.country !== 'object') {
    quote.store_information.country = { name: '' };
  }
  const deposit = Number(quote.store_information.deposit);
  if (!Number.isFinite(deposit)) {
    quote.store_information.deposit = 50;
  }
  normalizeStoreInformationBankFields(quote.store_information);
}

function ensureClientContactDefaults(quote: any) {
  if (!quote.client_contact || typeof quote.client_contact !== 'object') {
    quote.client_contact = { name: '', client_id: '', billing_address: {}, delivery_address: {} };
  }
  if (!quote.client_contact.billing_address || typeof quote.client_contact.billing_address !== 'object') {
    quote.client_contact.billing_address = {};
  }
  if (!quote.client_contact.delivery_address || typeof quote.client_contact.delivery_address !== 'object') {
    quote.client_contact.delivery_address = {};
  }
}

function ensureInteriorDesignerDefaults(quote: any) {
  if (!quote.interior_designer || typeof quote.interior_designer !== 'object') {
    quote.interior_designer = { name: '', email: '', phone: '' };
    return;
  }
  quote.interior_designer = {
    ...quote.interior_designer,
    name: quote.interior_designer.name || '',
    email: quote.interior_designer.email || '',
    phone: quote.interior_designer.phone || '',
  };
}

function ensureDeliveryGroupsDefaults(quote: any) {
  if (!Array.isArray(quote.delivery_groups)) {
    quote.delivery_groups = cloneDefault(DEFAULT_DELIVERY_GROUPS);
  }
}

type ParseJsonFieldsOptions = {
  useDefaultsOnError?: boolean;
  useDefaultsOnNull?: boolean;
};

function parseJsonFields(quote: any, opts: ParseJsonFieldsOptions = {}) {
  const { useDefaultsOnError = false, useDefaultsOnNull = false } = opts;

  for (const key of JSON_FIELDS) {
    const value = quote[key];
    if (typeof value !== 'string') continue;

    const raw = value.trim();
    if (!raw || raw === 'null') {
      if (useDefaultsOnNull) {
        quote[key] = cloneDefault(JSON_FIELD_DEFAULTS[key]);
      }
      continue;
    }

    try {
      const parsed = JSON.parse(raw);
      if (parsed === null && useDefaultsOnNull) {
        quote[key] = cloneDefault(JSON_FIELD_DEFAULTS[key]);
      } else {
        quote[key] = parsed;
      }
    } catch {
      console.warn(
        `[Quotation] Invalid JSON for ${key} on quote ${quote.id ?? quote.reference ?? 'unknown'}`,
      );
      if (useDefaultsOnError) {
        quote[key] = cloneDefault(JSON_FIELD_DEFAULTS[key]);
      }
    }
  }
}

/**
 * Parse léger pour le cache liste (fetch) : ne remplit pas les champs manquants
 * afin d'éviter d'écraser des données legacy à la sauvegarde.
 */
export function parseQuoteFieldsForList(quote: any) {
  if (!quote || typeof quote !== 'object') return quote;

  parseJsonFields(quote);

  if (!quote.status) {
    console.warn(`Quotation ${quote.id || 'unknown'} has no status, setting to DRAFT`);
    quote.status = 'DRAFT';
  }

  ensureSaleAssociateDefaults(quote);
  if (quote.sale_delegate_phone === undefined) quote.sale_delegate_phone = '';
  if (quote.sale_delegate_email === undefined) quote.sale_delegate_email = '';

  return quote;
}

/**
 * Normalisation complète pour édition / PDF (sur un clone, pas le cache liste).
 */
export function normalizeQuotation(quote: any) {
  if (!quote || typeof quote !== 'object') return quote;

  parseJsonFields(quote, { useDefaultsOnError: true, useDefaultsOnNull: true });

  if (!quote.status) {
    console.warn(`Quotation ${quote.id || 'unknown'} has no status, setting to DRAFT`);
    quote.status = 'DRAFT';
  }

  ensureSaleAssociateDefaults(quote);
  if (quote.sale_delegate_phone === undefined) quote.sale_delegate_phone = '';
  if (quote.sale_delegate_email === undefined) quote.sale_delegate_email = '';
  ensureStoreInformationDefaults(quote);
  if (!Array.isArray(quote.products_list)) quote.products_list = [];
  ensureClientContactDefaults(quote);
  ensureInteriorDesignerDefaults(quote);
  ensureDeliveryGroupsDefaults(quote);

  return quote;
}
